import {
  RecordingSpeakerRoleType,
  type RecordingMetadataSpeaker,
} from "@repo/app-types/entities/recording";
import type { Tool } from "@repo/app-types/entities/tool";
import { IconMessage } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { TextMessage } from "../../components/transcript/textMessage.js";
import { transcriptToolCallSpacing } from "../../components/transcript/transcriptSpacing.js";
import type { TranscriptSpeakerAvatarProps } from "../../components/transcript/transcriptSpeakerAvatar.js";
import type { QuoteMatchMap } from "./gradeValidationHelpers.js";
import { renderSpeakerName } from "./gradingHelpers.js";
import { GradingTranscriptAgentStep } from "./gradingTranscriptAgentStep.js";
import {
  resolveThreadEntryEvent,
  type GradingTranscriptTextConversationItem,
} from "./gradingTranscriptItems.js";
import { RecordingFragmentText } from "./snippetSharing/recordingFragmentText.js";
import type { FocusedSnippetState } from "./snippetSharing/useTranscriptSnippetSharing.js";

export interface GradingTranscriptTextConversationProps {
  conversation: GradingTranscriptTextConversationItem;
  speakers: readonly RecordingMetadataSpeaker[];
  /** For naming the tools the agent called. */
  tools: readonly Tool[] | undefined;
  activeSnippet?: FocusedSnippetState;
  quoteMatches?: QuoteMatchMap;
  searchMatches?: QuoteMatchMap;
}

/** A run of texts, and the agent's steps between them, between an interaction's calls. */
export const GradingTranscriptTextConversation: FC<
  GradingTranscriptTextConversationProps
> = (props) => {
  const {
    conversation,
    speakers,
    tools,
    activeSnippet,
    quoteMatches,
    searchMatches,
  } = props;
  const { entries, firstText } = conversation;
  const firstEntry = entries.at(0);
  if (!firstEntry) return null;
  const lastEntry = entries.at(-1) ?? firstEntry;
  const startedAt = DateTime.fromISO(resolveThreadEntryEvent(firstEntry).at);
  const endedAt = DateTime.fromISO(resolveThreadEntryEvent(lastEntry).at);
  const isSameDay = startedAt.hasSame(endedAt, "day");
  const dateLabel = isSameDay
    ? startedAt.toLocaleString(DateTime.DATE_MED)
    : `${startedAt.toLocaleString(DateTime.DATE_MED)} – ${endedAt.toLocaleString(DateTime.DATE_MED)}`;

  return (
    <section
      data-text-conversation-id={firstText.id}
      className="min-w-0 space-y-3 py-2"
    >
      <div className="flex justify-center">
        <h3 className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-full bg-gray-50 px-3 py-1 text-xs">
          <IconMessage
            aria-hidden="true"
            className="size-3.5 text-muted-foreground"
          />
          <span>Text conversation</span>
          <span className="text-muted-foreground">{dateLabel}</span>
        </h3>
      </div>
      <div
        className={twMerge(
          "flex flex-col rounded-2xl bg-muted px-4 py-8 [--tool-call-hover-bg:var(--color-gray-200)] [--transcript-avatar-shadow:0_0_6px_-2px_color-mix(in_srgb,currentColor_15%,transparent)] [--transcript-tool-item-gap:32px] [&>*+*]:mt-6 [&>[data-transcript-message]+[data-transcript-message]]:mt-12",
          transcriptToolCallSpacing,
        )}
      >
        {entries.map((entry) => {
          if (entry.type === "agent_step") {
            return (
              <div
                key={entry.step.id}
                data-transcript-tool-call={
                  entry.step.type === "tool_called" ? true : undefined
                }
                className={twMerge(
                  "flex min-w-0 text-xs",
                  entry.step.type === "tool_called"
                    ? "justify-center"
                    : "justify-end pr-11",
                )}
              >
                <GradingTranscriptAgentStep step={entry.step} tools={tools} />
              </div>
            );
          }

          const { text } = entry;
          const speakerName = renderSpeakerName(speakers, text.speaker_id);
          const speakerProps: TranscriptSpeakerAvatarProps =
            text.speaker_id === "agent"
              ? {
                  speakerName,
                  speakerRole: RecordingSpeakerRoleType.Representative,
                  isAgent: true,
                }
              : {
                  speakerName,
                  speakerRole: RecordingSpeakerRoleType.Customer,
                };
          const timestamp = DateTime.fromISO(text.at);
          return (
            <TextMessage
              key={text.id}
              {...speakerProps}
              timestamp={
                <time
                  dateTime={text.at}
                  title={timestamp.toLocaleString(DateTime.DATETIME_FULL)}
                >
                  {timestamp.toLocaleString(
                    isSameDay ? DateTime.TIME_SIMPLE : DateTime.DATETIME_MED,
                  )}
                </time>
              }
            >
              <p
                className="whitespace-pre-wrap"
                data-text-event-id={text.id}
                data-recording-id={text.transcript_target?.recording_id}
                data-fragment-index={text.transcript_target?.fragment_index}
                data-timestamp={text.transcript_target?.start_time}
              >
                {text.transcript_target ? (
                  <RecordingFragmentText
                    text={text.body}
                    recordingId={text.transcript_target.recording_id}
                    fragmentIndex={text.transcript_target.fragment_index}
                    activeSnippet={activeSnippet}
                    quoteMatches={quoteMatches}
                    searchMatches={searchMatches}
                  />
                ) : (
                  text.body
                )}
              </p>
            </TextMessage>
          );
        })}
      </div>
    </section>
  );
};
